import { motion, useAnimation } from 'framer-motion';
import React, { useContext, useEffect } from 'react';
import startCase from 'lodash/startCase';
import AppContext from '../../../context/AppContext';
import Form from '../../UI/Form/Form';
import Button from '../../UI/Button/Button';
import styles from './Results.module.scss';
import cup from '../../../assets/cup.svg';
import Share from '../../UI/Share/Share';

/* global APP_CONFIG */

const bannerVariants = {
    initial: { x: '-100%' },
    animate: { x: 0, transition: { duration: 0.5} },
    exit: { x: '100%', transition: { duration: 0.5, delay: .75  } }
};

const resultsVariants = {
    initial: { y: 50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: 50, opacity: 0 }
};

function Results() {
    const { 
        state: { 
            driverStatus, 
            drivers: { player }
        }, 
        dispatch 
    } = useContext( AppContext );
    const banner = useAnimation();
    const results = useAnimation();
    const cpu = driverStatus.find( ({ type }) => type === 'CPU' );

    function handleSubmit(data, response){
        dispatch({ type: 'SET_PLAYER', payload: response });
        dispatch({ type: 'RACE_RESTART' });
    }

    useEffect(() => {
        const sequence = async () => {
            await banner.start(bannerVariants.animate)
            await banner.start(bannerVariants.exit);
            await results.start(resultsVariants.animate);
        };
        sequence();
    }, [banner, results]);

    return (
        <div className={styles.container}>
            <motion.div 
                className={styles.banner} 
                initial="initial"
                animate={banner} 
                variants={bannerVariants}
                role="alert">
                <span className={styles.bannerInner}>
                    {player.isWinner ? `You win!` : `${cpu.name} wins!`}
                </span>
            </motion.div>

            <motion.div 
                className={styles.modal}
                initial="initial"
                animate={results} 
                exit="exit"
                variants={resultsVariants}
                transition={{ type: "spring", duration: .8 }}
                >
                <h2 className={styles.title}>Final results</h2>
                <div className={styles.body}>
                        <ul className={styles.results}>
                            {driverStatus.map(({ name, color, team, time, interval, hasStopped }, index) => {
                                const driverName = startCase(name.toLowerCase()).split(' ');
                                let firstName = driverName[0];

                                if( driverName.length > 1 ) {
                                    firstName = driverName.filter((name, index) => index !== driverName.length - 1).join(' ');
                                }

                                return (
                                    <li className={styles.driver} key={name} style={{ '--driver-color': color }}>
                                        <span className={styles.driverPos}>{index + 1}</span>

                                        <span className={styles.driverDetails}>
                                            <strong className={styles.driverName}>
                                                {firstName} {driverName.length > 1 && (<em>{driverName[driverName.length - 1]}</em>)}
                                            </strong>
                                            <span className={styles.driverTeam}>{team}</span>
                                        </span>

                                        <span className={styles.driverTime}>
                                            {index === 0 ? time.toFixed(3) : (
                                                hasStopped ? 'Out' : (
                                                    interval ? `+${Math.abs(interval).toFixed(3)}` : 'Interval'
                                                )
                                            )}
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>

                        <div className={styles.footer}>
                            <p className={styles.message}>
                                {player.isWinner &&
                                    <img src={cup} alt="" />
                                }
                                
                                {player.message}
                            </p>

                            <div className={styles.controls}>
                                {(player.isWinner || player.isPlayLimitReached) &&
                                    <Button as="a" href={APP_CONFIG.links.website.href} isPrimary={false}>
                                        {APP_CONFIG.links.website.label}
                                    </Button>
                                }

                                <Button as="a" href={APP_CONFIG.links.range.href} isPrimary={player.isWinner || (!player.isWinner && player.isPlayLimitReached)}>
                                    {APP_CONFIG.links.range.label}
                                </Button>

                                {!player.isWinner && !player.isPlayLimitReached &&
                                    <Form hideRecaptcha={true} data={player.formData} onSubmit={handleSubmit}>
                                        <Button type="submit">
                                            Race again
                                        </Button>
                                    </Form>
                                }
                            </div>

                            <Share text={APP_CONFIG.share.text} hashtags={APP_CONFIG.share.hashtags} emailBody={APP_CONFIG.share.emailBody}/>
                        </div>
                    </div>
            </motion.div>
        </div>
    );            
}

export default Results;